/**
 * The writing surface.
 *
 * A `<textarea>` with transparent text sitting exactly on top of a div that
 * renders the same characters, syntax-coloured. The author types into the
 * textarea and sees the div. Nothing intercepts the input path.
 *
 * ## Why this rather than a rich-text editor
 *
 * Everything a document editor has to get right and almost never does —
 * caret placement, selection across wrapped lines, undo grouping, IME
 * composition, dictation, autocorrect, mobile keyboards, spellcheck,
 * accessibility — is already correct in a textarea, because it is the
 * browser's own text control. A rich-text editor reimplements all of it on top
 * of contenteditable and pays a transaction pipeline per keystroke for the
 * privilege. Here the only per-keystroke work is re-colouring, and even that is
 * chunked (see ./lib/markdown-highlight) so a long sermon re-renders one
 * paragraph rather than the whole document.
 *
 * ## How the two layers stay aligned
 *
 * They must agree on every character and every box metric, or the text drifts
 * out from under the caret. The backdrop sits in normal flow and defines the
 * height; the textarea is absolutely positioned over it and never scrolls
 * itself — the page scrolls. Both share `.md-editor-text` in index.css, which
 * is the single source of font, size, line-height, padding and wrapping. Any
 * change to one is a change to both.
 *
 * The generous bottom padding is not only for comfort: it absorbs any
 * one-line disagreement between how a textarea and a div wrap their last line.
 */

import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  analyzeMarkdown,
  chunkHighlights,
  type HighlightChunk,
} from "@/lib/markdown-highlight";
import {
  applyEdit as applyTextareaEdit,
  insertAtCursor,
  replaceAll as replaceAllText,
} from "@/lib/textarea-edit";
import {
  continueBlock,
  indentLines,
  insertFootnote,
  insertLink,
  tabShouldIndent,
  toggleInline,
  type Edit,
  type TextSelection,
} from "@/lib/markdown-commands";

export interface MarkdownEditorHandle {
  getMarkdown: () => string;
  /**
   * Load a document. Deliberately not undoable and does not take focus — this
   * is the starting state, not an edit, and ⌘Z should never reach past it.
   */
  setMarkdown: (value: string) => void;
  /** Undoable whole-document swap, for draft recovery. */
  replaceAll: (value: string) => void;
  /** Undoable insertion at the caret. */
  insert: (text: string) => void;
  /** Run a command from ./lib/markdown-commands. */
  runEdit: (edit: Edit) => void;
  focus: () => void;
  getSelection: () => TextSelection;
  select: (start: number, end: number, options?: { reveal?: boolean }) => void;
  /** Viewport rect of a character offset, measured off the backdrop. */
  caretRect: (offset: number) => DOMRect | null;
  element: () => HTMLTextAreaElement | null;
}

interface MarkdownEditorProps {
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  /**
   * `document` — full writing surface (sermons).
   * `compact` — bordered field for notes/modals/sidebars; same highlighter,
   * smaller chrome (see `.md-editor-compact` in index.css).
   */
  variant?: "document" | "compact";
  /** Extra key handling after the editor's own shortcuts (e.g. ⌘↵ to save). */
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Fires on every edit, including programmatic ones. Keep it O(1). */
  onChange: (value: string) => void;
}

function sameChunk(a: HighlightChunk, b: HighlightChunk): boolean {
  if (a.text !== b.text || a.ranges.length !== b.ranges.length) return false;
  for (let index = 0; index < a.ranges.length; index += 1) {
    const left = a.ranges[index];
    const right = b.ranges[index];
    if (
      left.start !== right.start ||
      left.end !== right.end ||
      left.type !== right.type ||
      left.level !== right.level ||
      left.inSlide !== right.inSlide
    ) {
      return false;
    }
  }
  return true;
}

/**
 * One paragraph-ish run of the document.
 *
 * The comparator is what makes long documents viable: comparing a few dozen
 * ranges costs nothing next to reconciling the spans they describe, so every
 * chunk the author isn't currently typing in bails out.
 */
const Chunk = memo(
  function Chunk({ text, ranges }: HighlightChunk) {
    const parts: React.ReactNode[] = [];
    let cursor = 0;

    for (let index = 0; index < ranges.length; index += 1) {
      const range = ranges[index];
      if (range.start > cursor) parts.push(text.slice(cursor, range.start));
      // Heading depth, which the stylesheet fades the hashes by.
      const level = range.level ? ` md-level-${range.level}` : "";
      // Every character of a slide block carries this, so the block's wash is
      // continuous rather than showing through wherever another token painted.
      const slide = range.inSlide ? " md-in-slide" : "";
      parts.push(
        <span className={`md-token md-token-${range.type}${level}${slide}`} key={range.start}>
          {text.slice(range.start, range.end)}
        </span>,
      );
      cursor = range.end;
    }

    if (cursor < text.length) parts.push(text.slice(cursor));
    return <>{parts}</>;
  },
  (previous, next) => sameChunk(previous, next),
);

/** The text node and offset within it holding document offset `target`. */
function domPointAt(root: Node, target: number): { node: Text; offset: number } | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let seen = 0;
  let last: Text | null = null;
  let node = walker.nextNode() as Text | null;

  while (node) {
    const length = node.data.length;
    if (target <= seen + length) return { node, offset: target - seen };
    seen += length;
    last = node;
    node = walker.nextNode() as Text | null;
  }

  return last ? { node: last, offset: last.data.length } : null;
}

/** Nearest ancestor that actually scrolls. */
function scrollParent(element: HTMLElement | null): HTMLElement | null {
  let current = element?.parentElement ?? null;
  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    if (
      (overflow === "auto" || overflow === "scroll") &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  function MarkdownEditor(
    {
      className = "",
      placeholder = "",
      ariaLabel = "Document",
      id,
      variant = "document",
      onKeyDown,
      onChange,
    },
    ref,
  ) {
    const [value, setValue] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    /**
     * Where the caret belongs after a command. React re-rendering a controlled
     * textarea normally leaves the selection alone, because the DOM value
     * already matches — but a command that lands on a frame where it doesn't
     * would drop the caret to the end of the document mid-sentence, so the
     * intended selection is re-asserted after the commit rather than trusted to
     * survive it.
     */
    const pendingSelectionRef = useRef<TextSelection | null>(null);
    /**
     * The last value handed upward.
     *
     * A command's edit reaches `commit` twice: once from the `input` event the
     * browser fires as it performs the edit, and once from the caller that
     * asked for it. Both carry the same text, so the second is redundant — and
     * without this the page would count one command as two revisions and arm
     * its debounced save twice.
     */
    const committedRef = useRef("");

    const chunks = useMemo(() => chunkHighlights(value, analyzeMarkdown(value)), [value]);

    useLayoutEffect(() => {
      const pending = pendingSelectionRef.current;
      const element = textareaRef.current;
      if (!pending || !element) return;
      pendingSelectionRef.current = null;
      if (element.selectionStart !== pending.start || element.selectionEnd !== pending.end) {
        element.setSelectionRange(pending.start, pending.end);
      }
    });

    const commit = useCallback(
      (next: string, selection?: TextSelection) => {
        if (selection) pendingSelectionRef.current = selection;
        if (committedRef.current === next) return;
        committedRef.current = next;
        setValue(next);
        onChange(next);
      },
      [onChange],
    );

    const runEdit = useCallback(
      (edit: Edit) => {
        const element = textareaRef.current;
        if (!element) return;
        const result = applyTextareaEdit(element, edit.start, edit.end, edit.text, edit.caret);
        commit(result.value, edit.caret);
      },
      [commit],
    );

    const caretRect = useCallback((offset: number): DOMRect | null => {
      const backdrop = backdropRef.current;
      if (!backdrop) return null;

      const point = domPointAt(backdrop, offset);
      if (!point) return null;

      const range = document.createRange();
      try {
        // A collapsed range has no width in some engines and reports nothing.
        // Spanning a real character — forwards if there is one, otherwise
        // backwards — always produces a usable box.
        if (point.offset < point.node.data.length) {
          range.setStart(point.node, point.offset);
          range.setEnd(point.node, point.offset + 1);
        } else if (point.offset > 0) {
          range.setStart(point.node, point.offset - 1);
          range.setEnd(point.node, point.offset);
        } else {
          range.selectNode(point.node);
        }
      } catch {
        return null;
      }

      const rect = range.getBoundingClientRect();
      range.detach?.();
      return rect.width === 0 && rect.height === 0 ? null : rect;
    }, []);

    const select = useCallback(
      (start: number, end: number, options?: { reveal?: boolean }) => {
        const element = textareaRef.current;
        if (!element) return;
        const limit = element.value.length;
        const from = Math.max(0, Math.min(start, limit));
        const to = Math.max(from, Math.min(end, limit));

        element.focus({ preventScroll: true });
        element.setSelectionRange(from, to);

        if (options?.reveal === false) return;
        const rect = caretRect(from);
        const scroller = scrollParent(wrapperRef.current);
        if (!rect || !scroller) return;

        const viewport = scroller.getBoundingClientRect();
        scroller.scrollTo({
          // A third of the way down reads as "here it is" rather than pinning
          // the line to the very top edge.
          top: scroller.scrollTop + (rect.top - viewport.top) - scroller.clientHeight / 3,
          behavior: "smooth",
        });
      },
      [caretRect],
    );

    /**
     * Every member below is stable, and `getMarkdown` reads the DOM rather than
     * closing over `value`, so the handle is built once. That matters: the ref
     * is a state setter in the page, and a handle that changed identity per
     * keystroke would re-render the page per keystroke — the exact cost this
     * editor exists to avoid.
     */
    useImperativeHandle(
      ref,
      (): MarkdownEditorHandle => ({
        /**
         * The textarea is the buffer, so it is read directly — but it must not
         * be the only source.
         *
         * React detaches refs during the commit that removes the component,
         * while the page's unmount cleanup — which flushes any unsaved work —
         * runs afterwards. For that moment `textareaRef.current` is null. This
         * returning an empty string there would be read by the save path as
         * "the author deleted everything", and it would dutifully persist an
         * empty document over their sermon and clear the local recovery copy
         * along with it. `committedRef` outlives the DOM node and always holds
         * the last committed text, so the flush on the way out saves the
         * document rather than erasing it.
         */
        getMarkdown: () => textareaRef.current?.value ?? committedRef.current,
        setMarkdown: (next) => {
          pendingSelectionRef.current = null;
          committedRef.current = next;
          setValue(next);
        },
        replaceAll: (next) => {
          const element = textareaRef.current;
          if (!element) {
            setValue(next);
            onChange(next);
            return;
          }
          const result = replaceAllText(element, next);
          commit(result.value);
        },
        insert: (text) => {
          const element = textareaRef.current;
          if (!element) return;
          const result = insertAtCursor(element, text);
          commit(result.value);
        },
        runEdit,
        focus: () => textareaRef.current?.focus(),
        getSelection: () => ({
          start: textareaRef.current?.selectionStart ?? 0,
          end: textareaRef.current?.selectionEnd ?? 0,
        }),
        select,
        caretRect,
        element: () => textareaRef.current,
      }),
      [caretRect, commit, onChange, runEdit, select],
    );

    const currentSelection = (): TextSelection => {
      const element = textareaRef.current;
      return { start: element?.selectionStart ?? 0, end: element?.selectionEnd ?? 0 };
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const element = textareaRef.current;
      if (!element || event.nativeEvent.isComposing) {
        onKeyDown?.(event);
        return;
      }
      const mod = event.metaKey || event.ctrlKey;
      const text = element.value;
      const selection = currentSelection();

      if (event.key === "Enter" && !event.shiftKey && !mod) {
        const edit = continueBlock(text, selection);
        if (edit) {
          event.preventDefault();
          runEdit(edit);
          onKeyDown?.(event);
          return;
        }
      }

      if (event.key === "Tab") {
        if (!event.shiftKey && !tabShouldIndent(text, selection)) {
          onKeyDown?.(event);
          return;
        }
        event.preventDefault();
        runEdit(indentLines(text, selection, event.shiftKey ? -1 : 1));
        onKeyDown?.(event);
        return;
      }

      if (mod && event.altKey && event.key.toLowerCase() === "f") {
        event.preventDefault();
        runEdit(insertFootnote(text, selection));
        onKeyDown?.(event);
        return;
      }

      if (mod && !event.altKey) {
        switch (event.key.toLowerCase()) {
          case "b":
            event.preventDefault();
            runEdit(toggleInline(text, selection, "**"));
            onKeyDown?.(event);
            return;
          case "i":
            event.preventDefault();
            runEdit(toggleInline(text, selection, "*"));
            onKeyDown?.(event);
            return;
          case "k": {
            event.preventDefault();
            const url = window.prompt("Link URL", "https://");
            if (url) runEdit(insertLink(text, selection, url));
            onKeyDown?.(event);
            return;
          }
        }
      }

      onKeyDown?.(event);
    };

    const shellClass =
      variant === "compact"
        ? `md-editor md-editor-compact ${className}`
        : `md-editor ${className}`;

    return (
      <div className={shellClass} ref={wrapperRef}>
        <div className="md-editor-backdrop md-editor-text" ref={backdropRef} aria-hidden="true">
          {chunks.map((chunk, index) => (
            <Chunk key={index} text={chunk.text} ranges={chunk.ranges} />
          ))}
          {/* `pre-wrap` gives a trailing newline no height, so the backdrop
              would come up one line short of the textarea. */}
          {value.endsWith("\n") ? "​" : null}
        </div>

        {!value && placeholder ? (
          <div className="md-editor-placeholder md-editor-text" aria-hidden="true">
            {placeholder}
          </div>
        ) : null}

        <textarea
          ref={textareaRef}
          id={id}
          className="md-editor-input md-editor-text"
          value={value}
          aria-label={ariaLabel}
          autoCapitalize="sentences"
          autoCorrect="on"
          autoComplete="off"
          spellCheck
          onChange={(event) => commit(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  },
);
